/**
 * Waitlist Service
 *
 * API client for lesson waitlist: join, leave, list entries.
 */

import { ApiResponse, WaitlistEntry } from '@court-sync/shared';
import { apiGet, apiPost, apiDelete, publicApiGet } from './apiClient';

export interface WaitlistGuestStudentPayload {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: 'male' | 'female' | 'other';
  skillLevel?: 'beginner' | 'intermediate' | 'advanced';
  medicalNotes?: string;
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
  isSelf?: boolean;
}

export interface JoinWaitlistParams {
  lessonId: string;
  termId: string;
  academyId: string;
  studentId?: string;
  studentData?: WaitlistGuestStudentPayload;
  customerFirstName?: string;
  customerLastName?: string;
  customerEmail?: string;
}

export const waitlistService = {
  async joinWaitlist(params: JoinWaitlistParams): Promise<
    ApiResponse<{ waitlistEntryId?: string; waitlistEntry?: WaitlistEntry }>
  > {
    const body: Record<string, unknown> = {
      lessonId: params.lessonId,
      termId: params.termId,
      academyId: params.academyId,
    };
    if (params.studentData) {
      body.studentData = params.studentData;
      body.customerFirstName = params.customerFirstName;
      body.customerLastName = params.customerLastName;
      body.customerEmail = params.customerEmail;
    } else if (params.studentId) {
      body.studentId = params.studentId;
    }
    return apiPost<{ waitlistEntryId?: string; waitlistEntry?: WaitlistEntry }>('/public/waitlist', body);
  },

  async leaveWaitlist(entryId: string): Promise<ApiResponse> {
    return apiDelete(`/waitlist/${entryId}`);
  },

  async getWaitlistForLesson(
    lessonId: string,
    academyId: string
  ): Promise<ApiResponse<WaitlistEntry[]>> {
    return apiGet<WaitlistEntry[]>('/waitlist', {
      params: { lessonId, academyId },
    });
  },

  async getWaitlistForAcademy(academyId: string): Promise<ApiResponse<WaitlistEntry[]>> {
    return apiGet<WaitlistEntry[]>('/waitlist', {
      params: { academyId },
    });
  },

  async getMyWaitlistEntries(academyId?: string): Promise<ApiResponse<WaitlistEntry[]>> {
    const params = academyId ? { academyId } : {};
    return apiGet<WaitlistEntry[]>('/waitlist/my-entries', { params });
  },

  async validateWaitlistClaim(entryId: string, lessonId?: string): Promise<
    ApiResponse<{ entry: WaitlistEntry; lessonId: string; academyId: string }>
  > {
    const params: Record<string, string> = { entryId };
    if (lessonId) params.lessonId = lessonId;
    return publicApiGet<{ entry: WaitlistEntry; lessonId: string; academyId: string }>(
      '/public/waitlist/validate-claim',
      { params }
    );
  },
};
